/**
 * Landing Page (signed out)
 *
 * What a person sees when they reach any page of this application without
 * a session. Before this existed the dashboard rendered anyway and every
 * panel failed its own API call, so the first thing you met was a wall of
 * errors rather than an explanation and a way in.
 *
 * The surface answers three questions and stops: what is this, why can I
 * not see it, and how do I get in (F1, F5). Signing in returns the person
 * to the page they originally asked for, so arriving signed-out costs them
 * no backtracking (F4).
 *
 * ADL: no data is rendered here and no input is accepted beyond a single
 * navigation, so the card/composition vocabulary does not apply — see
 * `frontend/src/surfaces/signed-out-landing.adl.json` and #72, the gap
 * filed for pre-authentication surfaces.
 *
 * `environment.motion` carries x-status: proposed, so it is NOT a gate and
 * nothing here treats it as one. What it is: the budget this surface chose
 * to hold itself to, so that if the stance is ratified unchanged there is
 * nothing to redo. A draft cannot bind, but it can be honoured early.
 */

import { useCallback, useState } from 'react';
import { FlowIllustration } from './FlowIllustration';
import './LandingPage.css';

interface LandingPageProps {
  /** Starts the Keystone sign-in redirect */
  onSignIn: () => void;
  /** Message from a failed sign-in attempt, surfaced instead of swallowed */
  authError?: string | null;
}

const CAPABILITIES = [
  {
    title: 'Where the quarter stands',
    body: 'Revenue against target, the cost base behind it, and whether the pace holds — for this quarter or any of the last seven.',
  },
  {
    title: 'Cash you can count on',
    body: 'Outstanding invoices by age, expected receipts at 30, 60 and 90 days, and how long customers actually take to pay.',
  },
  {
    title: 'Answers, not just figures',
    body: 'Compliance positions, alerts worth acting on, and scenario modelling for the decisions that change the number.',
  },
];

export function LandingPage({ onSignIn, authError }: LandingPageProps) {
  // Tempo (C11): the redirect leaves this tab, so the button has to
  // acknowledge the click itself or the last thing the person sees is an
  // unchanged screen.
  const [signingIn, setSigningIn] = useState(false);

  const handleSignIn = useCallback(() => {
    setSigningIn(true);
    // Hand the tab over only after the busy state has had a frame to
    // paint. Assigning location.href in the same tick starts the
    // navigation before React commits, so the click appeared to do
    // nothing — verified in the browser, not assumed (C11).
    requestAnimationFrame(() => onSignIn());
  }, [onSignIn]);

  return (
    <div className="landing">
      <header className="landing__bar">
        {/* LOGO-001: left aligned, never centred. The plate exists because
            the supplied mark is dark-ink and would vanish on Marine. */}
        <span className="landing__logo-plate">
          <img
            src="/alterspective-logo.png"
            alt="Alterspective"
            className="landing__logo"
          />
        </span>
      </header>

      <main className="landing__main" id="main-content">
        <div className="landing__grid">
          <div className="landing__copy">
            <p className="landing__eyebrow">Alterspective Finance</p>
            <h1 className="landing__title">
              The numbers behind the quarter, while there is still time to
              act on them.
            </h1>
            <p className="landing__lede">
              A live view of Alterspective&rsquo;s financial position, drawn
              straight from Xero. Revenue against target, cash coming in,
              and the cost base underneath — without waiting for month end.
            </p>

            {authError && (
              <p className="landing__error" role="alert">
                {authError}
              </p>
            )}

            <div className="landing__actions">
              <button
                type="button"
                className="landing__signin"
                onClick={handleSignIn}
                disabled={signingIn}
              >
                {signingIn ? 'Taking you to sign in…' : 'Sign in'}
              </button>
              <p className="landing__note">
                Sign in with your Alterspective account. This dashboard holds
                company financial data, so access is restricted to staff.
              </p>
            </div>
          </div>

          <div className="landing__art-frame">
            {/* Decoration: it restates the lede in pictures and carries no
                information of its own, so it is hidden from assistive tech
                rather than narrated twice (AIRUN-044 Step 7). */}
            <FlowIllustration />
          </div>
        </div>

        <ul className="landing__cards">
          {CAPABILITIES.map((c) => (
            <li key={c.title} className="landing__card">
              <h2 className="landing__card-title">{c.title}</h2>
              <p className="landing__card-body">{c.body}</p>
            </li>
          ))}
        </ul>
      </main>

      <footer className="landing__footer">
        {/* CONF-03 neutral framing: the integration is named in the lede,
            where it tells a signed-out reader something useful. Repeating it
            in the footer of a public surface only advertises a dependency. */}
        <p>&copy; {new Date().getFullYear()} Alterspective Pty Ltd</p>
      </footer>
    </div>
  );
}
